import { observer } from 'mobx-react-lite';
import { useTranslation, Trans } from 'react-i18next';
import { useCreateBenefitSet } from '../hooks/useCreateBenefitSet';
import { BenefitCard } from '../../../../lib/components/atomic/dataDisplay/benefitCard';
import { useStepper } from '../../partials/actionStepperPartial';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useStore } from '../../../../hooks/useStore';
import { enqueueSnackbar } from 'notistack';
import { InformationDialog, Loading, Switch } from '../../../../lib/components';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { useCardOfferApplies } from '../hooks/useCardOfferApplies';
import { cardOfferAction } from '../../../../queries/pxtraCard';
import { CardOfferSaveError } from '../store/cardOffer';

export const Summary = observer(() => {
  const { t, i18n } = useTranslation();
  const { companyStore } = useStore();
  const {
    newBenefitSet,
    createBenefitSet,
    updateBenefitSet,
    saveCardOffer,
    isUpdating,
    isSavingCardOffer
  } = useCreateBenefitSet();
  const params = useParams();
  const cardOfferApplies = useCardOfferApplies();
  const { setNextButtonConfig, setBackButtonConfig, activeStep, setActiveStep } = useStepper();
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showRetractionDialog, setShowRetractionDialog] = useState(false);
  const [showCardRemovalDialog, setShowCardRemovalDialog] = useState(false);
  const [legalCheck, setLegalCheck] = useState(false);
  const confirmationNotes = Array.isArray(
    t('benefitSet:confirmationDialog:note3', { returnObjects: true })
  )
    ? (t('benefitSet:confirmationDialog:note3', { returnObjects: true }) as string[])
    : [];
  const retractionNotes = Array.isArray(
    t('benefitSet:retractionDialog:note3', { returnObjects: true })
  )
    ? (t('benefitSet:retractionDialog:note3', { returnObjects: true }) as string[])
    : [];

  const finish = async (benefitSetId: string) => {
    setShowConfirmationDialog(false);
    setShowRetractionDialog(false);
    if (!(await saveCardOffer(benefitSetId))) return;

    setActiveStep(activeStep + 1);
  };

  const createButtonConfig = {
    text: t('benefitSet:createSet'),
    onClick: async () => {
      const result = await createBenefitSet(companyStore.company?.id ?? '');
      if (!result) return;

      await finish(result.id);
    }
  };

  const apply = async () => {
    if (!newBenefitSet.hasSetChanges) {
      await finish(newBenefitSet.editingBenefitSetId ?? '');
    } else if (newBenefitSet.changedFields.includes('selectedBenefits')) {
      setShowConfirmationDialog(true);
    } else {
      await update();
    }
  };

  const removesCard = () =>
    cardOfferAction(
      newBenefitSet.cardOfferBaseline,
      newBenefitSet.desiredCardOffer(cardOfferApplies)
    ).kind === 'delete';

  const editButtonConfig = {
    text: t('custom:apply'),
    disabled: newBenefitSet.changedFields.length === 0,
    onClick: async () => {
      if (removesCard()) {
        setShowCardRemovalDialog(true);
        return;
      }
      await apply();
    }
  };

  const cardOfferSaveErrorText = ({ code, permanent }: CardOfferSaveError) => {
    const reasonKey = [`benefitSet:cardOfferApiError:${code}`, `benefitSet:apiError:${code}`].find(
      (key) => code && i18n.exists(key)
    );
    const reason = reasonKey ? t(reasonKey) : undefined;
    const fallback = permanent
      ? t('benefitSet:cardOfferSaveRejected')
      : t('benefitSet:cardOfferSaveErrorDescription');

    return reason ? `${t('benefitSet:cardOfferSaveSetSaved')} ${reason}` : fallback;
  };

  const retryCardOfferButtonConfig = {
    text: t('benefitSet:cardOfferRetry'),
    onClick: () => finish(newBenefitSet.editingBenefitSetId ?? '')
  };

  const linkMapping = {
    '0': 'https://www.gesetze-im-internet.de/agg/',
    '2': 'https://support.pxtra.de/platform/kann-ich-meinen-mitarbeitenden-bereits-genutzte-benefits-wieder-entziehen'
  };

  const update = async () => {
    try {
      await updateBenefitSet(
        newBenefitSet.editingBenefitSetId ?? '',
        () => finish(newBenefitSet.editingBenefitSetId ?? ''),
        () => setShowRetractionDialog(true)
      );
    } catch (error) {
      enqueueSnackbar(t('benefitSet:couldNotBeUpdated'), { variant: 'error' });
    }
  };

  // Re-registers after a failed card offer save so the retry never creates a
  // second set, and whenever `cardOfferApplies` changes: the handlers close
  // over it, and it settles only once the flag and the booked benefits load.
  useEffect(() => {
    if (newBenefitSet.cardOfferSavePending) {
      setNextButtonConfig(retryCardOfferButtonConfig);
      return;
    }
    setNextButtonConfig(newBenefitSet.editingBenefitSetId ? editButtonConfig : createButtonConfig);
  }, [newBenefitSet.cardOfferSavePending, newBenefitSet.changedFields.length, cardOfferApplies]);

  // The retry saves only the card offer: an edit made after going back would
  // be lost with it, so the way back stays closed until the offer is saved.
  useEffect(() => {
    setBackButtonConfig({ disabled: newBenefitSet.cardOfferSavePending });
    return () => setBackButtonConfig({});
  }, [newBenefitSet.cardOfferSavePending, setBackButtonConfig]);

  const cardOfferKnown =
    params.type !== 'edit_benefit_set' ||
    newBenefitSet.cardOfferLoadedFor === newBenefitSet.editingBenefitSetId;
  const showCardOffer =
    cardOfferApplies && newBenefitSet.selectedBenefits.includes('sachbezug') && cardOfferKnown;
  const desiredCardOffer = newBenefitSet.desiredCardOffer(cardOfferApplies);
  const cardOfferSummaryKey = () => {
    if (!desiredCardOffer.card_enabled) return 'benefitSet:cardOfferSummaryVoucher';

    return desiredCardOffer.physical_card_enabled
      ? 'benefitSet:cardOfferSummaryCardPhysical'
      : 'benefitSet:cardOfferSummaryCard';
  };

  return (
    <>
      {newBenefitSet.cardOfferSaveError && !isSavingCardOffer && (
        <div className="mb-6">
          <FeedbackAlert
            severity="error"
            title={t('benefitSet:cardOfferSaveErrorTitle')}
            description={cardOfferSaveErrorText(newBenefitSet.cardOfferSaveError)}
          />
        </div>
      )}
      <div className="flex gap-x-8">
        <div className="w-1/2 flex flex-col gap-y-4">
          <div className="text-sm font-medium">{t('benefitSet:description')}</div>
          <div
            className={`rounded-xl bg-grey-50 p-4 ${!newBenefitSet.description_de && 'text-grey-600'}`}
          >
            {newBenefitSet.description_de || t('benefitSet:descriptionMissing')}
          </div>
        </div>
        <div className="w-1/2 flex flex-col gap-y-4">
          <div className="flex gap-x-2 items-center">
            <div className="text-sm font-medium">{t('benefitSet:selectedBenefits')}</div>
            <div className="w-6 h-6 text-xs rounded-full bg-grey-50 flex items-center">
              <div className="m-auto">{newBenefitSet.selectedBenefits.length}</div>
            </div>
          </div>
          <div className="max-h-[522px] overflow-y-auto flex flex-col gap-y-2">
            {newBenefitSet.selectedBenefits.map((benefit) => (
              <BenefitCard key={benefit} benefit={benefit} />
            ))}
          </div>
          {showCardOffer && (
            <div className="flex flex-col gap-y-4">
              <div className="text-sm font-medium">{t('benefitSet:cardOfferSummaryLabel')}</div>
              <div className="rounded-xl bg-grey-50 p-4">{t(cardOfferSummaryKey())}</div>
            </div>
          )}
        </div>
      </div>
      <InformationDialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        onConfirm={async () => await update()}
        onCancel={() => setShowConfirmationDialog(false)}
        title={t('benefitSet:confirmationDialog:title')}
        confirmButtonText={t('custom:apply_changes')}
        cancelButtonText={t('common:CANCEL')}
        confirmButtonDisabled={!legalCheck}
        content={
          <div className="flex flex-col">
            <div className="pb-4">{t('benefitSet:confirmationDialog:note1')}</div>
            <div className="font-medium pb-3">{t('benefitSet:confirmationDialog:note2')}</div>
            <ul className="m-0 pl-4 pb-6 flex flex-col gap-y-2">
              {confirmationNotes.map((note, index) => (
                <li key={note}>
                  {note.includes('<0>') ? (
                    <Trans
                      i18nKey={note}
                      defaults={note}
                      components={[
                        <a
                          key="link"
                          href={
                            linkMapping[index.toString() as keyof typeof linkMapping] ??
                            'https://www.gesetze-im-internet.de/agg/'
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="text-heaven-600 no-underline"
                          aria-label="open link"
                        />
                      ]}
                    />
                  ) : (
                    note
                  )}
                </li>
              ))}
            </ul>
            <div className="flex gap-x-4">
              <Switch
                className="ml-0 mt-0"
                onChange={(event, checked) => {
                  setLegalCheck(checked);
                }}
              />
              <div className="text-sm">{t('benefitSet:confirmationDialog:legalNote')}</div>
            </div>
          </div>
        }
      />
      <InformationDialog
        open={showCardRemovalDialog}
        onClose={() => setShowCardRemovalDialog(false)}
        onConfirm={async () => {
          setShowCardRemovalDialog(false);
          await apply();
        }}
        onCancel={() => setShowCardRemovalDialog(false)}
        title={t('benefitSet:cardRemovalDialog:title')}
        confirmButtonText={t('benefitSet:cardRemovalDialog:confirm')}
        cancelButtonText={t('common:CANCEL')}
        content={<div>{t('benefitSet:cardRemovalDialog:description')}</div>}
      />
      <InformationDialog
        open={isUpdating}
        content={
          <div className="flex flex-col gap-y-6 items-center">
            <Loading />
            <div className="flex flex-col items-center gap-y-1">
              <div className="font-bold">{t('common:changesProcessing')}</div>
              <div className="text-sm text-grey-600">{t('custom:patience')}</div>
            </div>
          </div>
        }
      />
      <InformationDialog
        open={isSavingCardOffer}
        content={
          <div className="flex flex-col gap-y-6 items-center">
            <Loading />
            <div className="flex flex-col items-center gap-y-1">
              <div className="font-bold">{t('benefitSet:cardOfferSavingTitle')}</div>
              <div className="text-sm text-grey-600">{t('benefitSet:cardOfferSavingHint')}</div>
            </div>
          </div>
        }
      />
      <InformationDialog
        open={showRetractionDialog}
        onClose={() => setShowRetractionDialog(false)}
        onConfirm={async () => {
          try {
            newBenefitSet.forceRetract = true;
            await updateBenefitSet(
              newBenefitSet.editingBenefitSetId ?? '',
              () => finish(newBenefitSet.editingBenefitSetId ?? ''),
              () => enqueueSnackbar(t('benefitSet:updateFailed'), { variant: 'error' })
            );
          } catch (error) {
            enqueueSnackbar(t('benefitSet:couldNotBeUpdated'), { variant: 'error' });
          }
        }}
        onCancel={() => setShowRetractionDialog(false)}
        title={t('benefitSet:retractionDialog:title')}
        confirmButtonText={t('custom:apply_changes')}
        cancelButtonText={t('common:CANCEL')}
        content={
          <div className="flex flex-col">
            <div className="pb-4">{t('benefitSet:retractionDialog:note1')}</div>
            <div className="font-medium pb-3">{t('benefitSet:retractionDialog:note2')}</div>
            <ul className="m-0 pl-4 pb-6 flex flex-col gap-y-2">
              {retractionNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
            <FeedbackAlert
              severity="warning"
              description={t('benefitSet:retractionDialog:importantNote')}
            />
          </div>
        }
      />
    </>
  );
});
